import type { ReactNode } from "react";

import {
  SITE_URL,
  orgNode,
  faqJsonld,
  breadcrumbJsonld,
  webPageNode,
  type LearnFAQ,
  type LearnPageData,
  type LearnSection,
  type LearnSource,
  type TOCItem,
  type CTABand,
} from "@/pages/learn/learnShared";
import type { RelatedLink } from "@/components/RelatedPages";

/**
 * Spanish learn article factory. Mirrors learnFactory.makeArticle but:
 *  - Spanish breadcrumb root ("Inicio" / "Educación para pacientes")
 *  - canonical points to /es/learn/<slug>
 *  - webPageNode inLanguage es-US
 *  - sections rendered from plain text (same [[text|/path]] link syntax)
 *
 * Clinical claims, stats, citations and FDA wording are preserved verbatim
 * from the English source — only the surrounding prose is translated.
 */

export interface EsPlainSection {
  heading: string;
  paragraphs?: string[];
  paragraphsAfter?: string[];
  list?: string[];
  table?: { headers: string[]; rows: string[][] };
  links?: { to: string; text: string; suffix?: string }[];
}

function renderEsSection(s: EsPlainSection): LearnSection {
  const body: ReactNode[] = [];

  if (s.paragraphs) {
    s.paragraphs.forEach((p) => {
      const parts = p.split(/(\[\[[^\]]+\]\])/g);
      body.push(
        <p key={body.length}>
          {parts.map((part, i) => {
            const match = part.match(/^\[\[(.+)\|(.+)\]\]$/);
            if (match) {
              return (
                <a
                  href={match[2]}
                  className="font-medium text-accent underline underline-offset-2 hover:opacity-80"
                >
                  {match[1]}
                </a>
              );
            }
            return <span key={i}>{part}</span>;
          })}
        </p>,
      );
    });
  }

  if (s.list) {
    body.push(
      <ul key={body.length} className="mt-4 space-y-2">
        {s.list.map((item, i) => (
          <li
            key={i}
            className="relative pl-5 leading-relaxed text-foreground before:absolute before:left-0 before:top-2.5 before:h-1.5 before:w-1.5 before:rounded-full before:bg-accent"
          >
            {item}
          </li>
        ))}
      </ul>,
    );
  }

  if (s.table) {
    body.push(
      <div key={body.length} className="mt-4 overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/30">
              {s.table.headers.map((h, i) => (
                <th
                  key={i}
                  className="px-3 py-2 text-left font-semibold text-primary"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {s.table.rows.map((row, i) => (
              <tr key={i} className="border-b border-border/50">
                {row.map((cell, j) => (
                  <td key={j} className="px-3 py-2 text-foreground/85">
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>,
    );
  }

  if (s.paragraphsAfter) {
    s.paragraphsAfter.forEach((p) => {
      const parts = p.split(/(\[\[[^\]]+\]\])/g);
      body.push(
        <p key={body.length} className="mt-4">
          {parts.map((part, i) => {
            const match = part.match(/^\[\[(.+)\|(.+)\]\]$/);
            if (match) {
              return (
                <a
                  href={match[2]}
                  className="font-medium text-accent underline underline-offset-2 hover:opacity-80"
                >
                  {match[1]}
                </a>
              );
            }
            return <span key={i}>{part}</span>;
          })}
        </p>,
      );
    });
  }

  if (s.links) {
    body.push(
      <p key={body.length} className="mt-3">
        {s.links.map((l, i) => (
          <span key={i}>
            {i > 0 && " · "}
            <a
              href={l.to}
              className="font-medium text-accent underline underline-offset-2 hover:opacity-80"
            >
              {l.text}
            </a>
            {l.suffix || ""}
          </span>
        ))}
      </p>,
    );
  }

  return { heading: s.heading, body: <>{body}</> };
}

export interface EsArticleInput {
  slug: string;
  title: string;
  description: string;
  canonical?: string;
  h1: string;
  breadcrumbLabel: string;
  crumbs?: { name: string; href: string }[];
  shortAnswer: string;
  keyTakeaways: string[];
  sections: EsPlainSection[];
  faqs: LearnFAQ[];
  sources: LearnSource[];
  about?: object | object[];
  relatedLinks: RelatedLink[];
  reviewBlock?: LearnPageData["reviewBlock"];
  toc?: TOCItem[];
  safetyBlock?: string;
  trademarkFooter?: string;
  ctaBand?: CTABand;
}

/** Spanish breadcrumbs: Inicio / Educación para pacientes / <label> */
export function esBaseCrumbs(label: string) {
  return [
    { name: "Inicio", href: "/es" },
    { name: "Educación para pacientes", href: "/es/learn" },
    { name: label, href: "" },
  ];
}

export function makeEsArticle(input: EsArticleInput): LearnPageData {
  const canonical = input.canonical || `/es/learn/${input.slug}`;
  return {
    slug: input.slug,
    title: input.title,
    description: input.description,
    canonical,
    h1: input.h1,
    breadcrumbLabel: input.breadcrumbLabel,
    crumbs: input.crumbs || esBaseCrumbs(input.breadcrumbLabel),
    shortAnswer: input.shortAnswer,
    keyTakeaways: input.keyTakeaways,
    sections: input.sections.map(renderEsSection),
    faqs: input.faqs,
    sources: input.sources,
    schemaJson: {
      "@context": "https://schema.org",
      "@graph": [
        {
          ...webPageNode({
            id: `${SITE_URL}${canonical}#webpage`,
            name: input.h1,
            url: `${SITE_URL}${canonical}`,
            description: input.description,
            about: input.about,
          }),
          inLanguage: "es-US",
        },
        orgNode,
        faqJsonld(input.faqs, `${SITE_URL}${canonical}#faq`),
        breadcrumbJsonld(input.crumbs || esBaseCrumbs(input.breadcrumbLabel)),
      ],
    },
    relatedLinks: input.relatedLinks,
    reviewBlock: input.reviewBlock || {
      writtenBy: "Neuro Wellness TMS Centers of America",
      reviewedBy: "Daniel Pietras, MD",
      published: "3 de noviembre de 2026",
      lastReviewed: "3 de noviembre de 2026",
      lastUpdated: "3 de noviembre de 2026",
    },
    toc: input.toc,
    safetyBlock: input.safetyBlock,
    trademarkFooter: input.trademarkFooter,
    ctaBand: input.ctaBand,
  };
}

/* ── Shared Spanish related-link sets ── */

export const esSaintRelated: RelatedLink[] = [
  {
    title: "Aviso de SAINT\u00ae: los programas parecidos no son SAINT\u00ae",
    href: "/es/learn/saint-disclaimer",
    description:
      "Cognitive FX y otros programas \u201cguiados por fMRI\u201d no son SAINT\u00ae",
  },
  {
    title: "SAINT\u00ae TMS acelerada",
    href: "/es/saint",
    description: "El protocolo de 5 d\u00edas guiado por fMRI que ofrecemos",
  },
  {
    title: "¿Qué es SAINT® TMS?",
    href: "/es/learn/what-is-saint-tms",
    description: "Autorización de la FDA, protocolo y evidencia",
  },
  {
    title: "Costo de SAINT® TMS",
    href: "/es/learn/saint-tms-cost",
    description: "Componentes del costo, pago directo y financiamiento",
  },
  {
    title: "¿Cubre el seguro SAINT® TMS?",
    href: "/es/learn/does-insurance-cover-saint-tms",
    description: "Cobertura, autorización previa y verificación de beneficios",
  },
  {
    title: "Solicitar una consulta",
    href: "/es/saint",
    description: "Reserve una consulta gratuita con nuestro equipo clínico",
  },
];
